import {
    pxItemTypeGuards,
    ShareBaseDocument,
    ShareBaseFolder,
    ShareBaseItem,
    ShareBaseLibrary,
    DocumentRevision
} from 'phoenix/domain';
import { AuthService } from 'phoenix/services';
import { IUriPathService } from 'phoenix/services/uriPathService';
import { LibraryService } from 'sharebase/services';
import {
    AsyncOpHandle,
    AsyncOpStatus,
    CompletedAsyncOp,
    MimeType,
    OneOrMany
} from 'util/domain';
import { AsyncOpService } from 'util/services';

interface ZipDownloadBody {
    ZipFileName:string;
    DocumentIds:number[];
    FolderIds:number[];
}
export type DownloadOpHandle = AsyncOpHandle<AsyncOpStatus, string>;


/**
 * @ngdoc service
 * @name sharebase.sbDownloadService
 *
 * @description
 * Exposes methods for downloading ShareBase content. */

export class DownloadService {   
    public static $inject = [
        '$window',
        '$q',
        '$http',
        'pxAuthService',
        'pxUriPathService',
        'sbLibraryService',
        'asyncOpService'
    ];

    /**
	 * Check if there are any async download jobs currently running.
	 */
	public get hasPendingDownloads() {
		return Object.keys(this.asyncOpService.handles).length > 0;
    }
    
    constructor(
        private $window:ng.IWindowService,
        private $q:ng.IQService,
        private $http:ng.IHttpService,
        private pxAuthService:AuthService,
        private pxUriPathService:IUriPathService,
        private sbLibraryService:LibraryService,
        private asyncOpService:AsyncOpService
    ){        
    }

    /**
     * Synchronously download a document that has been generated on the fly.
     * For example: can be used to download CSV returned by the API
     *
     * @param documentContent The document content to download.
     * @param mimeType Type specified after 'data:attachment' in the URI
     * @param filename The name of the resulting file
     */
    public downloadDynamicDocument(documentContent:Blob, filename:string):void;
    public downloadDynamicDocument(documentContent:string, filename:string, mimeType:MimeType):void;
    public downloadDynamicDocument(documentContent:string|Blob, filename:string, mimeType:MimeType = 'application/json') {
        let fileBlob = typeof documentContent === 'string' ? 
            new Blob([documentContent], { type: mimeType }) :
            documentContent;
       
        // Download attribute isn't recognized by IE, so filename would be a guid if not
        // using the method below.  Also, IE and Edge disallow navigating to data URIs, so Blobs must be used
        if(this.$window.navigator.msSaveOrOpenBlob) {
            this.$window.navigator.msSaveBlob(fileBlob, filename);
        }
        else {
            let blobUri = this.$window.URL.createObjectURL(fileBlob);
            this.downloadByUri(blobUri, filename, false);
            this.$window.URL.revokeObjectURL(blobUri);
        }
    }

    
    /**
     * Download a single document synchronously, or a zip archive 
     * containing file of folders, documents, library contents asynchronously.
     *
     * @param items The items being downloaded. Can either be a collection of items
     * or a single document, library, or folder.
     * @param context The name to use for downloads containing more than one item.
     */
    public downloadItems(items:OneOrMany<ShareBaseItem>, context:string) {
        if (Array.isArray(items) && items.length === 1) {
            items = items[0];
        }
        
        let p:ng.IPromise<void|DownloadOpHandle> = this.$q.resolve();
        const shouldZip = Array.isArray(items) || 
            pxItemTypeGuards.isShareBaseFolder(items) || 
            pxItemTypeGuards.isShareBaseLibrary(items);        

        if(shouldZip){
            p = this.createZipDownloadHandle(items, context);
        }
        else if(pxItemTypeGuards.isShareBaseDocument(items) ||  pxItemTypeGuards.isDocumentRevision(items)) {
            this.downloadItem(items, context);            
        }
        else {
            throw new Error('Could not download items');
        }

        return p;            
    }

    private downloadItem(item:ShareBaseDocument|DocumentRevision, context?:string){
        const uri = pxItemTypeGuards.isDocumentRevision(item) ? 
            item.Links.ContentUri_v2 :
            item.Embedded.LatestFile.Links.Content_v2;       

        if(!context){
            context = pxItemTypeGuards.isDocumentRevision(item) ? item.OriginalFileName : item.DocumentName;
        }

        this.downloadByUri(uri, context);
    }

    private createZipDownloadHandle(items:OneOrMany<ShareBaseItem>, context:string){
        let p:ng.IPromise<ZipDownloadBody>;
        const zipUri = this.pxUriPathService.resolvePath('/async/f/zipcontent');

        if (Array.isArray(items)) {   
            p = this.$q.resolve(this.getDownloadRequestForMultiselect(items, context));
        } 
        else if (pxItemTypeGuards.isShareBaseFolder(items)) {
            p = this.$q.resolve(this.getDownloadRequestBodyForFolders(items, context));
        } 
        else if (pxItemTypeGuards.isShareBaseLibrary(items)) {
            p = this.getDownloadRequestBodyForLibrary(items, context);
        }

        return p
            .then(body =>this.$http.post<void>(zipUri, body))
            .then(res => {
                const location = res.headers('Location');
                return this.asyncOpService.getStatusUntilComplete<CompletedAsyncOp, string>(location, {
                    resultTransform: res => res.data.Links.ResultUri
                });
            });      
    }

    private getDownloadRequestBodyForLibrary(lib: ShareBaseLibrary, contextName:string = lib.LibraryName) {
        return this.sbLibraryService
            .getLibraryFolders(lib)
            .then(folders => this.getDownloadRequestBodyForFolders(folders, contextName));
    }

    private getDownloadRequestBodyForFolders(folders: OneOrMany<ShareBaseFolder>, zipName?:string):ZipDownloadBody {
        if (!Array.isArray(folders)) {
            zipName = zipName || folders.FolderName;
            folders = [folders];
        }

        return{
            ZipFileName: zipName,
            DocumentIds: [],
            FolderIds: folders.map(folder => folder.FolderId)
        }
    }

    private getDownloadRequestBodyForDocuments(documents:ShareBaseDocument[], zipName:string):ZipDownloadBody {
        return {
            ZipFileName: zipName,
            FolderIds: [],
            DocumentIds: documents.map(doc => doc.DocumentId)
        };
    }

    private getDownloadRequestForMultiselect(items: ShareBaseItem[], zipName:string):ZipDownloadBody {
        const docRequest = this.getDownloadRequestBodyForDocuments(items.filter(pxItemTypeGuards.isShareBaseDocument), zipName);
        const folderRequest = this.getDownloadRequestBodyForFolders(items.filter(pxItemTypeGuards.isShareBaseFolder), zipName);

        return {
            ZipFileName: zipName,
            DocumentIds: docRequest.DocumentIds,
            FolderIds: folderRequest.FolderIds
        };
    }


    /**
     * Download content from a URI.
     */
    public downloadByUri(uri:string, filename?:string, includeToken:boolean = true){      
        if(includeToken){
            uri = this.addAuthToken(uri);
        }
        
        let anchor = angular.element('<a/>');
        anchor.attr({
            href: uri,
            download: filename
        });

        // need to append the anchor element in order for FF to work
        angular.element(this.$window.document.body).append(anchor);
        anchor[0].click();
        anchor.remove();
    }

    private addAuthToken(uri:string){
        let authKey = this.pxAuthService.token.type === 'Bearer' ? 'access_token' : 'auth';
        let qsParams = (uri.split('?')[1] || '')
            .split('&')
            .filter(param => param.length > 0);

        // split off any params already on the URI
        uri = uri.split('?')[0];

        // add on auth param
        qsParams.push(`${authKey}=${this.pxAuthService.token.value}`);

        return `${uri}?${qsParams.join('&')}`;
    }
}


// WEBPACK FOOTER //
// ./src/sharebase/services/downloadService.ts